import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, Flame, Gift, Lock, ChevronDown } from 'lucide-react';
import { DAILY_CHALLENGES } from '../data/dailyChallenges';
import { useGame } from '../context/GameContext';

const ChallengesPage = () => {
  const { activeChallenge, dailyStatus, claimDailyReward, completedChallengeIds } = useGame();
  const [timeLeft, setTimeLeft] = useState('');

  // --- COUNTDOWN TIMER ---
  useEffect(() => {
    const calculateTime = () => {
      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const diff = tomorrow - now;

      const h = Math.floor(diff / (1000 * 60 * 60));
      const m = Math.floor((diff / (1000 * 60)) % 60);
      const s = Math.floor((diff / 1000) % 60);

      return `${h}h ${m}m ${s}s`;
    };

    const timer = setInterval(() => setTimeLeft(calculateTime()), 1000);
    setTimeLeft(calculateTime());
    return () => clearInterval(timer);
  }, []);

  const isActiveCompleted = !!activeChallenge && dailyStatus.completed;
  const isActiveClaimed = !!activeChallenge && dailyStatus.claimed;

  // Put active challenge first (hero), rest in list
  const activeId = activeChallenge?.id;
  const restChallenges = DAILY_CHALLENGES.filter((c) => c.id !== activeId);

  return (
    <div className="relative h-full w-full bg-black overflow-hidden flex flex-col">

      {/* ===== CYBERPUNK BACKGROUND ===== */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%]
          bg-[radial-gradient(circle,rgba(139,92,246,0.35),rgba(0,0,0,0.9)_55%)]"
          animate={{ rotate: 360 }}
          transition={{ duration: 220, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-0
          bg-[linear-gradient(120deg,transparent,rgba(34,211,238,0.25),transparent)]"
          animate={{ x: ['-120%', '120%'] }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_35%,rgba(0,0,0,0.96)_100%)]" />
      </div>

      {/* ===== STICKY HUD ===== */}
      <div className="sticky top-0 z-20 bg-black/70 backdrop-blur-xl border-b border-cyan-400/20 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <h1 className="text-3xl font-black tracking-widest uppercase flex items-center gap-2 text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">
            <Flame /> Daily Challenges
          </h1>

          <div className="flex items-center gap-3 bg-black/50 border border-cyan-400/40 px-4 py-2 rounded-full text-cyan-300 font-mono text-sm">
            <Clock size={16} /> RESET IN <span className="text-white">{timeLeft}</span>
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">

          {/* ===== HERO ACTIVE CHALLENGE ===== */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-3xl border border-purple-400/60 bg-gradient-to-r from-purple-900/50 to-black p-6 md:p-8 shadow-[0_0_35px_rgba(168,85,247,0.6)] overflow-hidden"
          >
            {/* subtle scanline */}
            <motion.div
              className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(34,211,238,0.08),transparent)]"
              animate={{ y: ['-120%', '120%'] }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            />

            <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex items-start gap-4">
                <div className="p-4 rounded-2xl bg-black/60 border border-cyan-400/30">
                  {isActiveClaimed ? (
                    <CheckCircle className="text-emerald-400 w-9 h-9" />
                  ) : (
                    <Gift className="text-cyan-300 w-9 h-9" />
                  )}
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-black uppercase tracking-widest bg-cyan-400 text-black px-2 py-1 rounded">
                      ACTIVE TODAY
                    </span>

                    {isActiveCompleted && !isActiveClaimed && (
                      <span className="text-[11px] font-black uppercase tracking-widest bg-emerald-400 text-black px-2 py-1 rounded animate-pulse">
                        COMPLETED
                      </span>
                    )}

                    {isActiveClaimed && (
                      <span className="text-[11px] font-black uppercase tracking-widest bg-slate-700 text-slate-200 px-2 py-1 rounded">
                        DONE
                      </span>
                    )}
                  </div>

                  <h2 className="mt-3 text-2xl md:text-3xl font-black text-white">
                    {activeChallenge?.title ?? 'No active challenge'}
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-cyan-200/70">
                    {activeChallenge?.desc ?? 'Active challenge not found in context.'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-start md:items-end gap-3 min-w-[200px]">
                <div className="text-xs font-bold text-cyan-300 uppercase tracking-widest">REWARD</div>
                <div className="text-lg font-black font-mono text-purple-300 border border-purple-400/30 bg-purple-500/10 px-4 py-2 rounded-xl">
                  {activeChallenge?.reward ?? '--'}
                </div>

                {activeChallenge && isActiveCompleted && !isActiveClaimed && (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={claimDailyReward}
                    className="w-full md:w-auto bg-gradient-to-r from-purple-500 to-cyan-400 text-black font-black px-6 py-3 rounded-xl shadow-[0_0_18px_rgba(34,211,238,0.9)] flex items-center justify-center gap-2"
                  >
                    <Gift size={16} /> CLAIM REWARD
                  </motion.button>
                )}

                {activeChallenge && isActiveClaimed && (
                  <div className="text-emerald-400 font-bold flex items-center gap-2 border border-emerald-400/30 bg-emerald-900/20 px-4 py-2 rounded-xl">
                    CLAIMED <CheckCircle size={16} />
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* ===== SCROLL HINT ===== */}
          <div className="mt-8 flex items-center justify-center text-cyan-200/50 text-xs font-mono gap-2">
            <ChevronDown size={14} /> Browse all challenges
          </div>

          {/* ===== TIMELINE LIST (ALL 50) ===== */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {restChallenges.map((challenge, idx) => {
              const isUnlocked = completedChallengeIds?.includes(challenge.id);
              return (
                <motion.div
                  key={challenge.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(0.25, idx * 0.01) }}
                  className={`relative rounded-2xl border ${isUnlocked ? 'border-emerald-500/30 bg-emerald-900/10' : 'border-cyan-400/15 bg-slate-950/40'} backdrop-blur-md px-5 py-4 overflow-hidden`}
                >
                  {/* left neon rail */}
                  <div className={`absolute left-0 top-0 h-full w-[3px] ${isUnlocked ? 'bg-emerald-500' : 'bg-gradient-to-b from-purple-500/60 via-cyan-400/50 to-transparent'}`} />

                  {/* locked overlay vibe (but still readable list) */}
                  {!isUnlocked && <div className="absolute inset-0 bg-black/20 pointer-events-none" />}

                  <div className="relative flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 p-2 rounded-xl border ${isUnlocked ? 'bg-emerald-500/20 border-emerald-500/50' : 'bg-black/50 border-slate-700'}`}>
                        {isUnlocked ? <CheckCircle size={16} className="text-emerald-400" /> : <Lock size={16} className="text-slate-400" />}
                      </div>

                      <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300/70">
                          Challenge #{challenge.id}
                        </div>
                        <div className="mt-1 text-sm font-bold text-white">
                          {challenge.title}
                        </div>
                        <div className="mt-1 text-xs text-cyan-200/55">
                          {challenge.desc}
                        </div>
                      </div>
                    </div>

                    <div className="text-right min-w-[110px]">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-300/60">
                        Reward
                      </div>
                      <div className="mt-1 text-xs font-mono text-purple-300 border border-purple-400/20 bg-purple-500/10 px-3 py-1 rounded-lg inline-block">
                        {challenge.reward}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ChallengesPage;
